/**
 * CRM-Sales Event Bridge Service
 *
 * Implements the mycelium protocol for economy of movement integration.
 * Provides event-driven communication between CRM and Sales Portal services.
 *
 * Principles:
 * - Loose coupling: Services emit and listen to events without direct dependencies
 * - Natural growth: Add new listeners without modifying existing services
 * - Resilience: Event failures don't cascade across the system
 * - Audit trail: All events logged for compliance and debugging
 */

import EventEmitter from 'eventemitter3';
import { logger } from '@/lib/logger';
import { typedSupabase } from '@/lib/typed-supabase';
import type { IService, ServiceResponse } from '../core/service-registry';
import { createSuccessResponse, createErrorResponse, createServiceError } from '../core/service-registry';
import type {
  CRMSalesEventType,
  BaseEventPayload,
  EventHandler,
  CRMSalesEventLog,
  OpportunityClosedWonPayload,
  TransactionCreatedPayload,
  CommissionCalculatedPayload,
  XeroInvoiceSyncedPayload,
  XeroSyncConflictPayload,
} from '@flowstate/types/events/crm-sales-events';
import { EVENT_CONFIGS } from '@flowstate/types/events/crm-sales-events';
import { salesTransactionService } from '../core/sales-portal/sales-transaction.service';
// import { commissionCalculationService } from '../core/sales-portal/commission-calculation.service';
import { notificationCRUD } from '@flowstate/core';
import { opportunityService } from '@flowstate/core';

// ============================================================================
// SERVICE INTERFACE
// ============================================================================

export interface ICRMSalesEventBridge extends IService {
  emitEvent<T>(payload: Omit<BaseEventPayload<T>, 'timestamp'>): Promise<ServiceResponse<void>>;
  markEventProcessed(eventId: string): Promise<ServiceResponse<void>>;
  markEventFailed(eventId: string, error: string): Promise<ServiceResponse<void>>;
  replayFailedEvents(limit?: number): Promise<ServiceResponse<number>>;
  getEventLog(filters?: EventLogFilters): Promise<ServiceResponse<CRMSalesEventLog[]>>;
}

export interface EventLogFilters {
  eventType?: CRMSalesEventType;
  status?: 'pending' | 'processed' | 'failed' | 'retrying';
  sourceService?: string;
  entityId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}

// ============================================================================
// SERVICE IMPLEMENTATION
// ============================================================================

export class CRMSalesEventBridge extends EventEmitter implements ICRMSalesEventBridge {
  readonly name = 'CRMSalesEventBridge';
  private static instance: CRMSalesEventBridge;

  private constructor() {
    super();
  }

  static getInstance(): CRMSalesEventBridge {
    if (!CRMSalesEventBridge.instance) {
      CRMSalesEventBridge.instance = new CRMSalesEventBridge();
    }
    return CRMSalesEventBridge.instance;
  }

  async initialize(): Promise<void> {
    logger.info('🌐 Initializing CRM-Sales Event Bridge (Mycelium Protocol)...');

    // Register default event listeners
    this.registerDefaultListeners();

    // Start background job for failed event replay
    this.startFailedEventReplayJob();

    logger.info('✅ CRM-Sales Event Bridge initialized successfully');
  }

  async dispose(): Promise<void> {
    logger.info('🧹 Disposing CRM-Sales Event Bridge...');
    this.removeAllListeners();
  }

  // ============================================================================
  // EVENT EMISSION
  // ============================================================================

  /**
   * Emit an event to all registered listeners and persist to database.
   * Implements economy of movement - data flows naturally through the network.
   */
  async emitEvent<T>(
    payload: Omit<BaseEventPayload<T>, 'timestamp'>
  ): Promise<ServiceResponse<void>> {
    const startTime = Date.now();

    try {
      const fullPayload: BaseEventPayload<T> = {
        ...payload,
        timestamp: new Date(),
      };

      logger.debug('Event emitted', {
        eventType: payload.eventType,
        source: payload.source,
        entityType: payload.entityType,
        entityId: payload.entityId,
        actorId: payload.actorId,
      });

      // Emit to in-memory listeners (synchronous for critical events, async for others)
      const config = EVENT_CONFIGS[payload.eventType];
      if (!config?.enabled) {
        logger.warn('Event type disabled', { eventType: payload.eventType });
        return createSuccessResponse(undefined, {
          duration: Date.now() - startTime,
          cached: false,
        });
      }

      // Emit event
      if (config.async) {
        // Non-blocking - fire and forget
        setImmediate(() => this.emit(payload.eventType, fullPayload));
      } else {
        // Blocking - wait for listeners to complete
        await this.emitAsync(payload.eventType, fullPayload);
      }

      // Persist to database for audit trail and async processing
      await this.logEvent(fullPayload);

      return createSuccessResponse(
        undefined,
        {
          duration: Date.now() - startTime,
          cached: false,
        }
      );
    } catch (error) {
      logger.error('Failed to emit event', {
        error,
        eventType: payload.eventType,
        source: payload.source,
      });

      return createErrorResponse(
        createServiceError(
          'EVENT_EMIT_FAILED',
          error instanceof Error ? error.message : 'Unknown error',
          this.name,
          { eventType: payload.eventType, error, duration: Date.now() - startTime }
        )
      );
    }
  }

  /**
   * Emit event and wait for all listeners to complete.
   * Used for synchronous events (e.g., opportunity.closed_won).
   */
  private async emitAsync(eventType: string, payload: unknown): Promise<void> {
    const listeners = this.listeners(eventType);
    if (listeners.length === 0) {
      logger.debug('No listeners registered for event', { eventType });
      return;
    }

    const promises = listeners.map(async (listener) => {
      try {
        await listener(payload);
      } catch (error) {
        logger.error('Event listener failed', {
          eventType,
          error,
          listenerName: listener.name,
        });
        // Don't throw - one listener failure shouldn't affect others
      }
    });

    await Promise.all(promises);
  }

  /**
   * Log event to database for audit trail.
   * Implements persistent mycelium memory.
   */
  private async logEvent<T>(payload: BaseEventPayload<T>): Promise<void> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (typedSupabase as any).from('crm_sales_events').insert({
        event_type: payload.eventType,
        event_data: payload.data as Record<string, unknown>,
        source_service: payload.source,
        source_entity_type: payload.entityType,
        source_entity_id: payload.entityId,
        actor_id: payload.actorId,
        metadata: payload.metadata,
        processing_status: 'pending',
        retry_count: 0,
      });

      if (error) {
        logger.error('Failed to log event to database', { error, eventType: payload.eventType });
      }
    } catch (error) {
      logger.error('Failed to log event', {
        error,
        eventType: payload.eventType,
      });
    }
  }

  // ============================================================================
  // EVENT STATUS MANAGEMENT
  // ============================================================================

  /**
   * Mark event as successfully processed.
   */
  async markEventProcessed(eventId: string): Promise<ServiceResponse<void>> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (typedSupabase as any)
        .from('crm_sales_events')
        .update({
          processing_status: 'processed',
          processed_at: new Date().toISOString(),
        })
        .eq('id', eventId);

      if (error) {
        throw error;
      }

      return createSuccessResponse(undefined);
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'EVENT_UPDATE_FAILED',
          error instanceof Error ? error.message : 'Unknown error',
          this.name,
          { eventId, error }
        )
      );
    }
  }

  /**
   * Mark event as failed with error message.
   */
  async markEventFailed(eventId: string, errorMessage: string): Promise<ServiceResponse<void>> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (typedSupabase as any)
        .from('crm_sales_events')
        .update({
          processing_status: 'failed',
          error_message: errorMessage,
        })
        .eq('id', eventId);

      if (error) {
        throw error;
      }

      logger.error('Event marked as failed', { eventId, errorMessage });

      return createSuccessResponse(undefined);
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'EVENT_UPDATE_FAILED',
          error instanceof Error ? error.message : 'Unknown error',
          this.name,
          { eventId, error }
        )
      );
    }
  }

  /**
   * Replay failed events for recovery.
   * Implements mycelium resilience - damaged nodes can regenerate.
   */
  async replayFailedEvents(limit: number = 100): Promise<ServiceResponse<number>> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: failedEvents, error } = await (typedSupabase as any)
        .from('crm_sales_events')
        .select('*')
        .eq('processing_status', 'failed')
        .lt('retry_count', 3)
        .order('created_at', { ascending: true })
        .limit(limit);

      if (error) {
        throw error;
      }

      if (!failedEvents || failedEvents.length === 0) {
        logger.info('No failed events to replay');
        return createSuccessResponse(0);
      }

      let replayedCount = 0;

      for (const event of failedEvents) {
        try {
          logger.info('Replaying failed event', {
            eventId: event.id,
            eventType: event.event_type,
            retryCount: event.retry_count,
          });

          // Re-emit event
          this.emit(event.event_type, {
            eventType: event.event_type,
            data: event.event_data,
            source: event.source_service,
            timestamp: new Date(event.created_at),
            entityType: event.source_entity_type,
            entityId: event.source_entity_id,
            actorId: event.actor_id,
            metadata: event.metadata,
          });

          // Increment retry count
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await (typedSupabase as any)
            .from('crm_sales_events')
            .update({
              retry_count: event.retry_count + 1,
              processing_status: 'retrying',
            })
            .eq('id', event.id);

          replayedCount++;
        } catch (error) {
          logger.error('Failed to replay event', {
            eventId: event.id,
            error,
          });
        }
      }

      logger.info('Failed events replayed', { replayedCount, totalFailed: failedEvents.length });

      return createSuccessResponse(replayedCount);
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'REPLAY_FAILED',
          error instanceof Error ? error.message : 'Unknown error',
          this.name,
          { error }
        )
      );
    }
  }

  /**
   * Get event log with filters.
   */
  async getEventLog(filters?: EventLogFilters): Promise<ServiceResponse<CRMSalesEventLog[]>> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let query = (typedSupabase as any).from('crm_sales_events').select('*');

      if (filters) {
        if (filters.eventType) {
          query = query.eq('event_type', filters.eventType);
        }
        if (filters.status) {
          query = query.eq('processing_status', filters.status);
        }
        if (filters.sourceService) {
          query = query.eq('source_service', filters.sourceService);
        }
        if (filters.entityId) {
          query = query.eq('source_entity_id', filters.entityId);
        }
        if (filters.startDate) {
          query = query.gte('created_at', filters.startDate);
        }
        if (filters.endDate) {
          query = query.lte('created_at', filters.endDate);
        }
      }

      query = query.order('created_at', { ascending: false }).limit(filters?.limit ?? 100);

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return createSuccessResponse(data as CRMSalesEventLog[]);
    } catch (error) {
      return createErrorResponse(
        createServiceError(
          'EVENT_LOG_FETCH_FAILED',
          error instanceof Error ? error.message : 'Unknown error',
          this.name,
          { error }
        )
      );
    }
  }

  // ============================================================================
  // CONVENIENCE METHODS FOR EVENT SUBSCRIPTION
  // ============================================================================

  /**
   * Subscribe to opportunity events.
   */
  onOpportunityCreated(handler: EventHandler): void {
    this.on('opportunity.created', handler);
  }

  onOpportunityStageChanged(handler: EventHandler): void {
    this.on('opportunity.stage_changed', handler);
  }

  onOpportunityClosedWon(handler: EventHandler<OpportunityClosedWonPayload>): void {
    this.on('opportunity.closed_won', handler);
  }

  /**
   * Subscribe to transaction events.
   */
  onTransactionCreated(handler: EventHandler<TransactionCreatedPayload>): void {
    this.on('transaction.created', handler);
  }

  onTransactionApproved(handler: EventHandler): void {
    this.on('transaction.approved', handler);
  }

  /**
   * Subscribe to commission events.
   */
  onCommissionCalculated(handler: EventHandler<CommissionCalculatedPayload>): void {
    this.on('commission.calculated', handler);
  }

  /**
   * Subscribe to Xero events.
   */
  onXeroInvoiceSynced(handler: EventHandler<XeroInvoiceSyncedPayload>): void {
    this.on('xero.invoice_synced', handler);
  }

  onXeroSyncConflict(handler: EventHandler<XeroSyncConflictPayload>): void {
    this.on('xero.sync_conflict', handler);
  }

  // ============================================================================
  // DEFAULT EVENT LISTENERS
  // ============================================================================

  /**
   * Register default event listeners for common integration flows.
   * Implements economy of movement - automatic data flow without manual intervention.
   */
  private registerDefaultListeners(): void {
    // Opportunity closed-won → Create sales transaction
    this.onOpportunityClosedWon(async (payload) => {
      try {
        const opportunity = payload.data;
        logger.info('Auto-creating transaction from closed-won opportunity', {
          opportunityId: opportunity.id,
          value: opportunity.value,
          clientId: opportunity.client_id,
        });

        // Extract team_id from opportunity metadata or use default
        const metadata = opportunity.metadata as Record<string, unknown> | undefined;
        const teamId = metadata?.team_id as string | undefined;
        if (!teamId) {
          logger.warn('Cannot auto-create transaction: no team_id in opportunity metadata', {
            opportunityId: opportunity.id,
          });
          return;
        }

        // Create sales transaction
        const transactionResult = await salesTransactionService.createSalesTransaction({
          team_id: teamId,
          opportunity_id: opportunity.id,
          client_id: opportunity.client_id,
          transaction_type: ((opportunity.metadata as Record<string, unknown>)?.deal_type as "other") || 'other',
          amount: opportunity.value,
          sales_rep_id: opportunity.sales_rep_id,
          closer_id: opportunity.sales_rep_id, // Same as sales rep by default
          description: `Auto-created from opportunity: ${opportunity.title}`,
          notes: `Opportunity closed-won on ${new Date(opportunity.actual_close_date ?? new Date()).toLocaleDateString()}`,
          metadata: {
            source: 'opportunity_closed_won',
            opportunity_id: opportunity.id,
            auto_created: true,
          },
        });

        if (transactionResult.error) {
          logger.error('Failed to auto-create transaction', {
            opportunityId: opportunity.id,
            error: transactionResult.error,
          });
          return;
        }

        logger.info('Successfully auto-created transaction', {
          opportunityId: opportunity.id,
          transactionId: transactionResult.data?.id,
          amount: opportunity.value,
        });

        // Emit transaction.created event for downstream processing
        if (transactionResult.data) {
          await this.emitEvent({
            eventType: 'transaction.created',
            data: transactionResult.data,
            source: 'CRMSalesEventBridge',
            entityType: 'sales_transaction',
            entityId: transactionResult.data.id,
            metadata: {
              triggered_by: 'opportunity.closed_won',
              opportunity_id: opportunity.id,
            },
          });
        }
      } catch (error) {
        logger.error('Failed to auto-create transaction', {
          opportunityId: payload.data.id,
          error,
        });
      }
    });

    // Transaction created → Calculate commissions
    this.onTransactionCreated(async (payload) => {
      try {
        const transaction = payload.data;
        logger.info('Auto-calculating commissions for new transaction', {
          transactionId: transaction.id,
          amount: transaction.amount,
        });

        // Commission calculation already happens in createSalesTransaction
        // but we emit the event for notification purposes

        // Collect user IDs who should be notified
        const userIds: string[] = [];
        if (transaction.sales_rep_id) {
          userIds.push(transaction.sales_rep_id);
        }
        if (transaction.closer_id && transaction.closer_id !== transaction.sales_rep_id) {
          userIds.push(transaction.closer_id);
        }

        // Get account manager from client
        if (transaction.client_id) {
          const { data: client } = await typedSupabase
            .from('clients')
            .select('assigned_to')
            .eq('id', transaction.client_id)
            .single();

          if (client?.assigned_to && !userIds.includes(client.assigned_to)) {
            userIds.push(client.assigned_to);
          }
        }

        // Send notifications to all involved parties
        for (const userId of userIds) {
          try {
            await notificationCRUD.createNotification({
              user_id: userId,
              type: 'COMMISSION_EARNED',
              title: 'New Commission Earned',
              message_preview: `You earned a commission from a ${transaction.transaction_type} transaction`,
              data: {
                message_full: `A new sales transaction has been created for $${transaction.amount}. Your commission has been calculated and will be included in your next payout.`,
                transaction_id: transaction.id,
                amount: transaction.amount,
                commission_amount: transaction.commission_amount,
                transaction_type: transaction.transaction_type,
              },
              entity_type: 'sales_transaction',
              entity_id: transaction.id,
            });
          } catch (notifError) {
            logger.warn('Failed to send commission notification', {
              userId,
              transactionId: transaction.id,
              error: notifError,
            });
          }
        }

        logger.info('Commission notifications sent', {
          transactionId: transaction.id,
          recipientCount: userIds.length,
        });
      } catch (error) {
        logger.error('Failed to process transaction creation', {
          transactionId: payload.data.id,
          error,
        });
      }
    });

    // Xero invoice synced → Update opportunity stage
    this.onXeroInvoiceSynced(async (payload) => {
      try {
        const { xeroInvoice, crmOpportunity } = payload.data;

        // Type guard for xeroInvoice
        if (typeof xeroInvoice !== 'object' || xeroInvoice === null) {
          logger.error('Invalid xeroInvoice in payload', { payload });
          return;
        }

        const invoice = xeroInvoice as Record<string, unknown>;
        if (invoice.status === 'PAID') {
          logger.info('Auto-updating opportunity stage from Xero invoice payment', {
            opportunityId: crmOpportunity.id,
            xeroInvoiceId: invoice.invoiceID,
            invoiceStatus: invoice.status,
          });

          // Update opportunity to closed-won if it's not already
          if (crmOpportunity.stage !== 'closed-won') {
            const updateResult = await opportunityService.updateOpportunity(crmOpportunity.id, {
              stage: 'closed-won',
              actual_close_date: new Date().toISOString(),
              metadata: {
                ...(crmOpportunity.metadata ? (crmOpportunity.metadata as Record<string, unknown>) : {}),
                xero_invoice_id: invoice.invoiceID as string,
                xero_invoice_number: invoice.invoiceNumber as string,
                closed_reason: 'Xero invoice marked as PAID',
                auto_closed: true,
              },
            });

            if (updateResult.error) {
              logger.error('Failed to auto-update opportunity from Xero invoice', {
                opportunityId: crmOpportunity.id,
                error: updateResult.error,
              });
              return;
            }

            logger.info('Successfully auto-updated opportunity from Xero invoice', {
              opportunityId: crmOpportunity.id,
              oldStage: crmOpportunity.stage,
              newStage: 'closed-won',
            });

            // Emit stage_changed event
            await this.emitEvent({
              eventType: 'opportunity.stage_changed',
              data: {
                opportunity: updateResult.data!,
                oldStage: crmOpportunity.stage,
                newStage: 'closed-won',
                reason: 'Xero invoice marked as PAID',
              },
              source: 'CRMSalesEventBridge',
              entityType: 'opportunity',
              entityId: crmOpportunity.id,
              metadata: {
                triggered_by: 'xero.invoice_synced',
                xero_invoice_id: invoice.invoiceID as string,
              },
            });
          }
        }
      } catch (error) {
        logger.error('Failed to auto-update opportunity from Xero', {
          opportunityId: payload.data.crmOpportunity.id,
          error,
        });
      }
    });

    logger.info('Default event listeners registered');
  }

  /**
   * Start background job to replay failed events.
   * Runs every 5 minutes to recover from transient failures.
   */
  private startFailedEventReplayJob(): void {
    setInterval(async () => {
      try {
        const result = await this.replayFailedEvents(50);
        if (result.data && result.data > 0) {
          logger.info('Background job replayed failed events', { count: result.data });
        }
      } catch (error) {
        logger.error('Background job failed to replay events', { error });
      }
    }, 5 * 60 * 1000); // Every 5 minutes

    logger.info('Failed event replay background job started');
  }
}

// ============================================================================
// EXPORT SINGLETON INSTANCE
// ============================================================================

export const crmSalesEventBridge = CRMSalesEventBridge.getInstance();
